/**
 * Beaches With Kids is an EVERGREEN RANKED GUIDE — the third page shape.
 * Not a hub (browse) and not a category page (compare): a ranked, opinionated
 * article built to rank for "best santa barbara beaches with kids" and to
 * carry affiliate and sponsor revenue.
 */

export const beachesIntro = {
  meta: '12 beaches · Updated May 2026 · 9 min read',
  deck:
    'Ranked by how a beach day with small kids actually goes — not by how the sand photographs. Calm water, real bathrooms, shade, and how far you carry everything from the car.',
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/31edd276-fe28-4275-a856-210d7dde2559.jpg',
  heroAlt:
    'A wide calm California beach in late afternoon with a palm-lined bluff in the distance',
  author: 'Rachel',
  published: 'Updated May 2026',
}

export const shortAnswer = {
  title: 'The short answer',
  body: 'For toddlers and anyone who cannot swim yet, go to East Beach or the Goleta Beach side of the slough — the water is flattest and the bathrooms are close. For kids old enough to entertain themselves, Arroyo Burro has a creek and driftwood and is the best all-round family beach in Santa Barbara. Skip Butterfly Beach with little ones: no bathrooms, no parking, and a shore break that knocks over adults.',
  picks: [
    { label: 'Best overall with kids', value: 'Arroyo Burro (Hendry’s)' },
    { label: 'Calmest water', value: 'East Beach' },
    { label: 'Best for tidepools', value: 'Carpinteria Bluffs' },
    { label: 'Best for a quick stop', value: 'Leadbetter Beach' },
  ],
}

export const timing = [
  {
    title: 'Go after 2pm, not before noon',
    body: 'May through July the marine layer often does not burn off until early afternoon. A 10am beach plan in June is usually a cold, grey beach plan.',
  },
  {
    title: 'Check the tide, seriously',
    body: 'At high tide several of these beaches have almost no dry sand. Low tide roughly doubles the usable beach and is the only time the tidepools work.',
  },
  {
    title: 'Wind picks up around 3pm in spring',
    body: 'March through May, the afternoon onshore wind is real. Harbor-side beaches stay calmer than the open-facing ones.',
  },
]

export interface RankedBeach {
  rank: number
  slug: string
  name: string
  area: string
  image?: string
  imageAlt?: string
  verdict: string
  body: string
  knowBefore: string
  water: string
  bathrooms: string
  shade: string
  parking: string
  carry: string
  bestFor: string
}

export const rankedBeaches: RankedBeach[] = [
  {
    rank: 1,
    slug: 'arroyo-burro-beach',
    name: 'Arroyo Burro (Hendry’s) Beach',
    area: 'The Mesa',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fe23e60c-729a-4a75-a529-eb5db937935e.jpg',
    imageAlt:
      'A shallow creek meeting the sand with driftwood scattered along a beach below sandstone bluffs',
    verdict: 'The best all-round family beach in Santa Barbara',
    body: 'The creek is the whole thing. It runs shallow and warm across the sand and kids will dam it, wade it and rebuild it for two hours without once asking to go in the ocean. Add driftwood to build with, a lifeguard in summer, bathrooms with actual doors, and a café at the top of the sand so nobody melts down on the walk back to the car.',
    knowBefore:
      'The lot fills by 11am on summer weekends. Overflow parking on Cliff Drive is free and adds a three-minute walk.',
    water: 'Moderate shore break',
    bathrooms: 'Yes, plus outdoor showers',
    shade: 'Some, at the bluff edge',
    parking: 'Free lot + street',
    carry: '2 min from the lot',
    bestFor: 'Ages 2–10',
  },
  {
    rank: 2,
    slug: 'east-beach',
    name: 'East Beach',
    area: 'Waterfront',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/907cd3da-3bb3-4835-b53d-50f30822a0f9.jpg',
    imageAlt:
      'A sheltered beach with very calm flat water and sailboat masts in the background',
    verdict: 'The calmest water, and the one to pick with a toddler',
    body: 'Wide, flat, and protected enough that the water is often barely moving. That matters more than anything else when you have a two-year-old who wants to walk straight into the ocean. The playground and the volleyball courts break up the day, and the bike path means you can arrive without dealing with parking at all.',
    knowBefore:
      'Metered lots run about $2.50/hour and add up fast. Park on Cabrillo past the bird refuge for free and walk the path in.',
    water: 'Very calm',
    bathrooms: 'Yes, several',
    shade: 'Palms only, bring a tent',
    parking: 'Metered lots',
    carry: '1–5 min',
    bestFor: 'Ages 0–6',
  },
  {
    rank: 3,
    slug: 'goleta-beach',
    name: 'Goleta Beach',
    area: 'Goleta',
    verdict: 'The easiest logistics of any beach on this list',
    body: 'Free parking directly beside the sand, a big grass picnic area with real shade, a playground, and a pier to walk out on. The slough side is protected and shallow enough for wading. It is not the prettiest beach in the county, and with small kids that turns out not to matter at all.',
    knowBefore:
      'Water quality drops after the first big rain of the season — check the county advisory before going in.',
    water: 'Calm on the slough side',
    bathrooms: 'Yes',
    shade: 'Real shade, grass area',
    parking: 'Free lot, right there',
    carry: '30 seconds',
    bestFor: 'All ages',
  },
  {
    rank: 4,
    slug: 'leadbetter-beach',
    name: 'Leadbetter Beach',
    area: 'Waterfront',
    verdict: 'The best quick stop, and where kids learn to surf',
    body: 'A crescent of sand tucked behind the harbor with a gentle break on the inside. Every learn-to-surf camp in town runs here for good reason. The bluff-top lawn above is a fine place to eat lunch when the sand has stopped being fun.',
    knowBefore:
      'Afternoon wind hits this beach harder than most in spring. Mornings are noticeably calmer here.',
    water: 'Gentle inside break',
    bathrooms: 'Yes',
    shade: 'Little',
    parking: 'Metered lot',
    carry: '2 min',
    bestFor: 'Ages 5–12',
  },
  {
    rank: 5,
    slug: 'carpinteria-state-beach',
    name: 'Carpinteria State Beach',
    area: 'Carpinteria',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2a267e8c-5578-4018-b2f5-d91f5aef0789.jpg',
    imageAlt:
      'Rocky tidepools at low tide with shallow reflective pools between dark rocks',
    verdict: 'Worth the drive for the tidepools and the seals',
    body: 'Locally known as the world’s safest beach, which is marketing, but the water genuinely is shallow and slow. Go at low tide for the tidepools at the south end, and between December and May the harbor seal rookery has pups visible from the bluff trail. Downtown Carpinteria is two blocks away for ice cream after.',
    knowBefore:
      'The seal rookery viewpoint is closed to the beach below during pupping season — stay on the bluff trail.',
    water: 'Very calm',
    bathrooms: 'Yes',
    shade: 'Some, campground side',
    parking: 'Paid lot or free street',
    carry: '3 min',
    bestFor: 'Ages 3–10',
  },
  {
    rank: 6,
    slug: 'butterfly-beach',
    name: 'Butterfly Beach',
    area: 'Montecito',
    verdict: 'Beautiful, and the wrong call with small kids',
    body: 'It is the prettiest beach in the county at sunset and we still would not take a toddler. There are no bathrooms, parking is a narrow strip of Channel Drive that fills before nine, and the shore break drops straight onto steep sand hard enough to knock over an adult. Come back without the kids, or when they are ten.',
    knowBefore:
      'At high tide the sand almost disappears entirely. Check the tide before you commit to the drive.',
    water: 'Steep, hard shore break',
    bathrooms: 'None',
    shade: 'None',
    parking: 'Street only, fills early',
    carry: '1 min',
    bestFor: 'Ages 10+',
  },
]

export const beachKit = {
  title: 'What we actually bring',
  note: 'Some links below earn a small commission at no extra cost to you. We only list things we own and have hauled across sand more than once.',
  items: [
    {
      name: 'Pop-up beach tent',
      why: 'There is almost no natural shade on any Santa Barbara beach. This is the difference between two hours and forty minutes.',
      price: '$60–90',
    },
    {
      name: 'Mesh tote instead of a beach bag',
      why: 'Sand falls straight through it. Nothing else changes the drive home as much.',
      price: '$25',
    },
    {
      name: 'Baby powder',
      why: 'Sounds odd, works perfectly — it takes wet sand straight off skin at the car.',
      price: '$4',
    },
    {
      name: 'Wagon with fat wheels',
      why: 'Standard stroller wheels sink. If you go to the beach weekly, this is the purchase.',
      price: '$120–180',
    },
  ],
}

export const beachesFaqs = [
  {
    question: 'What is the best beach in Santa Barbara for toddlers?',
    answer:
      'East Beach has the calmest water and the closest bathrooms, which are the two things that matter most under age three. Goleta Beach is a close second and has free parking right beside the sand plus a grass picnic area with real shade.',
  },
  {
    question: 'Which Santa Barbara beach has the calmest water for kids?',
    answer:
      'East Beach and the slough side of Goleta Beach are the flattest. Carpinteria State Beach is also very calm and shallow, and is worth the twenty-minute drive for the tidepools at low tide.',
  },
  {
    question: 'Are there bathrooms at Santa Barbara beaches?',
    answer:
      'Arroyo Burro, East Beach, Goleta Beach, Leadbetter and Carpinteria State Beach all have restrooms. Butterfly Beach and most of the Montecito beaches do not, which is the main reason we do not recommend them with small kids.',
  },
  {
    question: 'When is the best time of day to go to the beach here?',
    answer:
      'After 2pm from May through July, once the marine layer has burned off. Earlier in the day is often grey and cold. In late summer and fall, mornings are fine and less windy.',
  },
  {
    question: 'Do you have to pay to park at Santa Barbara beaches?',
    answer:
      'Arroyo Burro and Goleta Beach have free lots. East Beach and Leadbetter are metered at roughly $2.50 an hour. Carpinteria State Beach charges for the lot but has free street parking a few blocks in.',
  },
]

export const beachesTier3 = [
  {
    area: 'More Santa Barbara beaches',
    items: ['West Beach', 'Mesa Lane steps', 'Thousand Steps', 'Shoreline Park beach'],
  },
  {
    area: 'North of town',
    items: ['El Capitán State Beach', 'Refugio State Beach', 'Haskell’s Beach', 'Gaviota State Park'],
  },
  {
    area: 'South of town',
    items: ['Padaro Beach', 'Rincon Beach Park', 'Loon Point', 'Summerland Beach'],
  },
  {
    area: 'Related guides',
    items: ['Tidepooling with kids', 'Best sunset spots', 'Beach birthday party spots', 'Where to rent kids’ wetsuits'],
  },
]
